import React, { useCallback, useRef, useEffect, useState } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { getSpellchecker } from 'monaco-spellchecker';
import Typo from 'typo-js';
import { CompileDiagnostic } from '../../services/compileErrors';
import { ProjectFile } from '../../types';
import { OTComponents } from '../../types/ot';
import { UserPresence } from '../../types/ot';
import { monacoChangesToOTOp, otOpToMonacoEdits } from '../../services/otMonacoBridge';

interface CommentRange {
  id: string;
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
}

// Commands whose {…} argument contains prose that should be spellchecked
const CONTENT_COMMANDS = new Set([
  'emph', 'textbf', 'textit', 'textrm', 'textsl', 'textsc', 'underline',
  'caption', 'footnote', 'footnotetext',
  'section', 'subsection', 'subsubsection', 'paragraph', 'subparagraph',
  'chapter', 'part', 'title', 'author', 'date',
  'mbox', 'text', 'mathrm',
]);

/* eslint-disable no-template-curly-in-string */
const LATEX_COMMAND_SUGGESTIONS = [
  { label: '\\begin', insertText: '\\begin{${1:environment}}\n\t$0\n\\end{${1:environment}}', detail: 'Environment block' },
  { label: '\\section', insertText: '\\section{${1:title}}', detail: 'Section heading' },
  { label: '\\subsection', insertText: '\\subsection{${1:title}}', detail: 'Subsection heading' },
  { label: '\\subsubsection', insertText: '\\subsubsection{${1:title}}', detail: 'Subsubsection heading' },
  { label: '\\paragraph', insertText: '\\paragraph{${1:title}}', detail: 'Paragraph heading' },
  { label: '\\emph', insertText: '\\emph{${1:text}}', detail: 'Emphasized text' },
  { label: '\\textbf', insertText: '\\textbf{${1:text}}', detail: 'Bold text' },
  { label: '\\textit', insertText: '\\textit{${1:text}}', detail: 'Italic text' },
  { label: '\\cite', insertText: '\\cite{${1:key}}', detail: 'Citation' },
  { label: '\\ref', insertText: '\\ref{${1:label}}', detail: 'Cross-reference' },
  { label: '\\eqref', insertText: '\\eqref{${1:label}}', detail: 'Equation reference' },
  { label: '\\label', insertText: '\\label{${1:name}}', detail: 'Label anchor' },
  { label: '\\includegraphics', insertText: '\\includegraphics[width=${1:\\textwidth}]{${2:file}}', detail: 'Image include' },
  { label: '\\item', insertText: '\\item ${1:text}', detail: 'List item' },
  { label: '\\frac', insertText: '\\frac{${1:numerator}}{${2:denominator}}', detail: 'Fraction' },
  { label: '\\left(', insertText: '\\left( ${1:expression} \\right)', detail: 'Auto-sized parentheses' },
  { label: '\\left[', insertText: '\\left[ ${1:expression} \\right]', detail: 'Auto-sized brackets' },
  { label: '\\mathbf', insertText: '\\mathbf{${1:symbol}}', detail: 'Bold math symbol' },
  { label: '\\mathbb', insertText: '\\mathbb{${1:R}}', detail: 'Blackboard bold' },
];
/* eslint-enable no-template-curly-in-string */

/* eslint-disable no-template-curly-in-string */
const LATEX_MATH_MODE_SUGGESTIONS = [
  {
    label: '$...$',
    insertText: '${1:expression}',
    detail: 'Inline math',
    sortText: '0-inline-math',
    replaceAutoClosedPair: false,
  },
  {
    label: '$$...$$',
    insertText: '$$\n\t${1:expression}\n$$',
    detail: 'Display math',
    sortText: '1-display-math',
    replaceAutoClosedPair: true,
  },
];
/* eslint-enable no-template-curly-in-string */

function extractLatexLabels(files: ProjectFile[]): string[] {
  const labels = new Set<string>();

  for (const file of files) {
    if (file.type !== 'tex') continue;
    const pattern = /\\label\{([^}]+)\}/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(file.content)) !== null) {
      const value = match[1]?.trim();
      if (value) labels.add(value);
    }
  }

  return Array.from(labels).sort();
}

function extractBibKeys(files: ProjectFile[]): string[] {
  const keys = new Set<string>();

  for (const file of files) {
    if (file.type !== 'bib' && !file.name.toLowerCase().endsWith('.bib')) continue;
    const pattern = /@\w+\s*\{\s*([^,\s]+)\s*,/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(file.content)) !== null) {
      const value = match[1]?.trim();
      if (value) keys.add(value);
    }
  }

  return Array.from(keys).sort();
}

function latexTokenize(text: string): { word: string; pos: number }[] {
  const tokens: { word: string; pos: number }[] = [];
  let i = 0;
  let inMathInline = false;
  let inMathDisplay = false;

  while (i < text.length) {
    // Skip display math $$...$$
    if (text[i] === '$' && text[i + 1] === '$') {
      if (inMathDisplay) {
        inMathDisplay = false;
        i += 2;
        continue;
      } else {
        inMathDisplay = true;
        i += 2;
        continue;
      }
    }

    // Skip inline math $...$
    if (text[i] === '$' && !inMathDisplay) {
      inMathInline = !inMathInline;
      i++;
      continue;
    }

    // Skip content inside math mode
    if (inMathInline || inMathDisplay) {
      i++;
      continue;
    }

    // Skip comments (% to end of line)
    if (text[i] === '%') {
      while (i < text.length && text[i] !== '\n') i++;
      continue;
    }

    // Handle LaTeX commands
    if (text[i] === '\\') {
      i++; // skip backslash
      if (i >= text.length) continue;

      // Single-char escapes: \%, \$, \&, \#, \_, \{, \}, \^, \~
      if (!/[a-zA-Z@]/.test(text[i])) {
        // \( starts paren-delimited inline math
        if (text[i] === '(') {
          i++;
          while (i < text.length) {
            if (text[i] === '\\' && text[i + 1] === ')') { i += 2; break; }
            i++;
          }
          continue;
        }
        // \[ starts bracket-delimited display math
        if (text[i] === '[') {
          i++;
          while (i < text.length) {
            if (text[i] === '\\' && text[i + 1] === ']') { i += 2; break; }
            i++;
          }
          continue;
        }
        i++; // skip the single escaped character
        continue;
      }

      // Multi-char command name (e.g., \textbf, \begin)
      const cmdStart = i;
      while (i < text.length && /[a-zA-Z@]/.test(text[i])) i++;
      const cmdName = text.slice(cmdStart, i);

      // Skip optional * (e.g., \section*)
      if (i < text.length && text[i] === '*') i++;

      // Skip optional arguments [...]
      if (i < text.length && text[i] === '[') {
        let depth = 1;
        i++;
        while (i < text.length && depth > 0) {
          if (text[i] === '[') depth++;
          else if (text[i] === ']') depth--;
          i++;
        }
      }

      // Handle mandatory brace argument {...}
      if (i < text.length && text[i] === '{') {
        if (CONTENT_COMMANDS.has(cmdName)) {
          // Content commands: skip only the opening brace, let content be tokenized
          i++; // skip '{'
        } else {
          // Non-content commands: skip entire {...} block
          let depth = 1;
          i++;
          while (i < text.length && depth > 0) {
            if (text[i] === '{') depth++;
            else if (text[i] === '}') depth--;
            i++;
          }
        }
      }
      continue;
    }

    // Skip closing braces from content commands
    if (text[i] === '}') {
      i++;
      continue;
    }

    // Collect word characters
    if (/[a-zA-Z']/.test(text[i])) {
      const start = i;
      while (i < text.length && /[a-zA-Z']/.test(text[i])) i++;
      const word = text.slice(start, i).replace(/^'+|'+$/g, '');
      // Skip very short words (1-2 chars) and words that are all caps (likely acronyms)
      if (word.length >= 3 && word !== word.toUpperCase()) {
        tokens.push({ word, pos: start });
      }
      continue;
    }

    i++;
  }

  return tokens;
}

/** Derive a Monaco language ID from a file name's extension */
function getMonacoLanguage(fileName?: string): string {
  if (!fileName) return 'latex';
  const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
  switch (ext) {
    case '.py': return 'python';
    case '.js': return 'javascript';
    case '.ts': return 'typescript';
    case '.json': return 'json';
    case '.html': return 'html';
    case '.css': return 'css';
    case '.xml': return 'xml';
    case '.yaml': case '.yml': return 'yaml';
    case '.md': return 'markdown';
    case '.sh': case '.bash': return 'shell';
    case '.bib': return 'bibtex';
    case '.sty': case '.cls': case '.tex': return 'latex';
    case '.txt': return 'plaintext';
    default: return 'plaintext';
  }
}

interface LatexEditorProps {
  /** Active file ID — used to detect file switches and suppress spurious change events */
  fileId?: string;
  /** File name — used to derive Monaco language mode and control spellcheck */
  fileName?: string;
  value: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  onCompile?: () => void;
  readOnly?: boolean;
  canComment?: boolean;
  onAddComment?: (selection: {
    startLine: number;
    endLine: number;
    startColumn: number;
    endColumn: number;
    text: string;
  }) => void;
  onSelectComment?: (commentId: string) => void;
  selectedCommentId?: string | null;
  commentRanges?: CommentRange[];
  spellcheckEnabled?: boolean;
  compileDiagnostics?: CompileDiagnostic[];
  allFiles?: ProjectFile[];
  /** OT mode: when true, onChange is NOT called for local edits (OT handles sync) */
  otEnabled?: boolean;
  /** Called with OT operation when user types (only when otEnabled) */
  onOTChange?: (op: OTComponents, docLengthBefore: number) => void;
  /** Remote operations to apply to the editor */
  remoteOps?: Array<{ op: OTComponents; userId: string }>;
  /** Remote users for cursor/selection decorations */
  remoteUsers?: UserPresence[];
  /** Callback when cursor position changes (for presence) */
  onCursorChange?: (position: { lineNumber: number; column: number }) => void;
  /** Callback when selection changes (for presence) */
  onSelectionChange?: (selection: {
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  } | null) => void;
  /** Called after remote ops are applied via pushEditOperations, to sync state for compilation */
  onRemoteContentSync?: (content: string) => void;
  /** Persist Monaco cursor/scroll state across file and tab switches */
  viewState?: editor.ICodeEditorViewState | null;
  /** Called whenever Monaco is about to switch files or unmount */
  onViewStateChange?: (fileId: string, viewState: editor.ICodeEditorViewState | null) => void;
}

export function LatexEditor({
  fileId,
  fileName,
  value,
  onChange,
  onSave,
  onCompile,
  readOnly = false,
  canComment = false,
  onAddComment,
  onSelectComment,
  selectedCommentId,
  commentRanges = [],
  spellcheckEnabled = true,
  compileDiagnostics = [],
  allFiles = [],
  otEnabled = false,
  onOTChange,
  remoteOps,
  remoteUsers = [],
  onCursorChange,
  onSelectionChange,
  onRemoteContentSync,
  viewState,
  onViewStateChange,
}: LatexEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<any>(null);
  const decorationIdsRef = useRef<string[]>([]);
  const remoteCursorDecorationIdsRef = useRef<string[]>([]);
  const commentActionAddedRef = useRef(false);
  const clipboardActionsAddedRef = useRef(false);
  const spellcheckerRef = useRef<{ process: () => void; dispose: () => void } | null>(null);
  const typoRef = useRef<Typo | null>(null);
  const ignoredWordsRef = useRef<Set<string>>(new Set());
  const customDictionaryRef = useRef<Set<string>>(new Set());
  const [editorReady, setEditorReady] = useState(false);
  const lastRevealedDiagnosticRef = useRef<string | null>(null);
  const completionProviderDisposableRef = useRef<{ dispose: () => void } | null>(null);
  const labelSuggestionsRef = useRef<string[]>([]);
  const citationSuggestionsRef = useRef<string[]>([]);
  // OT: flag to suppress re-triggering onChange when applying remote edits
  const isRemoteEditRef = useRef(false);
  // File-switch detection: suppress change events when @monaco-editor/react replaces
  // the entire document via executeEdits() due to a value prop change (file switch).
  // Set during render (before child effects), cleared in our useEffect (after child effects).
  const isFileSwitchingRef = useRef(false);
  const prevFileIdRef = useRef(fileId);
  if (fileId !== prevFileIdRef.current) {
    isFileSwitchingRef.current = true;
  }
  const onOTChangeRef = useRef(onOTChange);
  onOTChangeRef.current = onOTChange;
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;
  const onViewStateChangeRef = useRef(onViewStateChange);
  onViewStateChangeRef.current = onViewStateChange;
  labelSuggestionsRef.current = extractLatexLabels(allFiles);
  citationSuggestionsRef.current = extractBibKeys(allFiles);

  // Clear file-switch flag after all child effects (including @monaco-editor/react's
  // executeEdits) have run. React runs child effects before parent effects, so by the
  // time this fires, the value-prop-driven model replacement is already complete.
  useEffect(() => {
    if (isFileSwitchingRef.current) {
      isFileSwitchingRef.current = false;
      prevFileIdRef.current = fileId;
    }
  });

  useEffect(() => {
    if (!editorReady) return;

    const editorInstance = editorRef.current;
    if (!editorInstance) return;

    const restoreViewStateFrame = window.requestAnimationFrame(() => {
      const nextViewState = viewStateRef.current;
      if (nextViewState) {
        editorInstance.restoreViewState(nextViewState);
        editorInstance.render();
        return;
      }

      editorInstance.setPosition({ lineNumber: 1, column: 1 });
      editorInstance.setScrollTop(0);
      editorInstance.setScrollLeft(0);
    });

    return () => {
      window.cancelAnimationFrame(restoreViewStateFrame);
      if (fileId) {
        onViewStateChangeRef.current?.(fileId, editorInstance.saveViewState());
      }
    };
  }, [editorReady, fileId]);

  const revealDiagnosticAtIndex = useCallback((index: number) => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    const diagnostic = compileDiagnostics[index];
    if (!editorInstance || !monaco || !diagnostic) return;

    const range = new monaco.Range(
      diagnostic.line,
      diagnostic.column,
      diagnostic.endLine,
      diagnostic.endColumn
    );

    editorInstance.setPosition({
      lineNumber: diagnostic.line,
      column: diagnostic.column,
    });
    editorInstance.revealRangeInCenter(range);
    editorInstance.focus();
  }, [compileDiagnostics]);

  const navigateDiagnostic = useCallback((direction: 1 | -1) => {
    const editorInstance = editorRef.current;
    if (!editorInstance || compileDiagnostics.length === 0) return;

    const position = editorInstance.getPosition();
    if (!position) {
      revealDiagnosticAtIndex(0);
      return;
    }

    const currentIndex = compileDiagnostics.findIndex((diagnostic) => (
      diagnostic.line === position.lineNumber && diagnostic.column === position.column
    ));

    if (currentIndex >= 0) {
      const nextIndex = (currentIndex + direction + compileDiagnostics.length) % compileDiagnostics.length;
      revealDiagnosticAtIndex(nextIndex);
      return;
    }

    if (direction === 1) {
      const nextIndex = compileDiagnostics.findIndex((diagnostic) => (
        diagnostic.line > position.lineNumber
        || (diagnostic.line === position.lineNumber && diagnostic.column > position.column)
      ));
      revealDiagnosticAtIndex(nextIndex >= 0 ? nextIndex : 0);
      return;
    }

    const previousIndex = [...compileDiagnostics].reverse().findIndex((diagnostic) => (
      diagnostic.line < position.lineNumber
      || (diagnostic.line === position.lineNumber && diagnostic.column < position.column)
    ));

    revealDiagnosticAtIndex(
      previousIndex >= 0
        ? compileDiagnostics.length - 1 - previousIndex
        : compileDiagnostics.length - 1
    );
  }, [compileDiagnostics, revealDiagnosticAtIndex]);

  const handleEditorDidMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;
      setEditorReady(true);

      // Register LaTeX language if not already registered
      if (!monaco.languages.getLanguages().some((lang) => lang.id === 'latex')) {
        monaco.languages.register({ id: 'latex' });

        // LaTeX syntax highlighting
        monaco.languages.setMonarchTokensProvider('latex', {
          tokenizer: {
            root: [
              // Comments
              [/%.*$/, 'comment'],
              // Commands
              [/\\[a-zA-Z@]+/, 'keyword'],
              // Math mode
              [/\$\$/, { token: 'string', next: '@mathDisplay' }],
              [/\$/, { token: 'string', next: '@mathInline' }],
              // Braces
              [/[{}]/, 'delimiter.bracket'],
              // Square brackets
              // eslint-disable-next-line no-useless-escape
              [/[\[\]]/, 'delimiter.square'],
            ],
            mathDisplay: [
              [/\$\$/, { token: 'string', next: '@pop' }],
              [/./, 'string'],
            ],
            mathInline: [
              [/\$/, { token: 'string', next: '@pop' }],
              [/./, 'string'],
            ],
          },
        });

        // LaTeX bracket matching and auto-closing
        monaco.languages.setLanguageConfiguration('latex', {
          comments: {
            lineComment: '%',
          },
          brackets: [
            ['{', '}'],
            ['[', ']'],
            ['(', ')'],
          ],
          autoClosingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '$', close: '$' },
          ],
          surroundingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '$', close: '$' },
          ],
        });
      }

      // Save first, then compile to match Overleaf-style behavior.
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        onSave?.();
        onCompile?.();
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
        onCompile?.();
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Slash, () => {
        editor.trigger('overleaf-shortcuts', 'editor.action.commentLine', {});
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space, () => {
        editor.trigger('overleaf-shortcuts', 'editor.action.triggerSuggest', {});
      });
      editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.KeyE, () => {
        navigateDiagnostic(1);
      });
      editor.addCommand(monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyE, () => {
        navigateDiagnostic(-1);
      });

      // Keep code actions accessible after switching back to the native browser menu.
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Period, () => {
        editor.trigger('spellcheck', 'editor.action.quickFix', {});
      });
      editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.Enter, () => {
        editor.trigger('spellcheck', 'editor.action.quickFix', {});
      });

      // OT: intercept content changes for operation generation
      editor.onDidChangeModelContent((event) => {
        if (isRemoteEditRef.current) return; // Skip remote edits
        if (isFileSwitchingRef.current) return; // Skip file-switch executeEdits
        if (!onOTChangeRef.current) return;

        const model = editor.getModel();
        if (!model) return;

        // Calculate doc length before this change
        const docLengthBefore = event.changes.reduce((len, change) => {
          return len + change.rangeLength - change.text.length;
        }, model.getValue().length);

        const op = monacoChangesToOTOp(event.changes, docLengthBefore);
        if (op.length > 0) {
          onOTChangeRef.current(op, docLengthBefore);
        }
      });

      // Cursor and selection tracking for presence
      editor.onDidChangeCursorPosition((e) => {
        onCursorChange?.({ lineNumber: e.position.lineNumber, column: e.position.column });
      });

      editor.onDidChangeCursorSelection((e) => {
        const sel = e.selection;
        if (sel.isEmpty()) {
          onSelectionChange?.(null);
        } else {
          onSelectionChange?.({
            startLineNumber: sel.startLineNumber,
            startColumn: sel.startColumn,
            endLineNumber: sel.endLineNumber,
            endColumn: sel.endColumn,
          });
        }
      });

      if (!completionProviderDisposableRef.current) {
        completionProviderDisposableRef.current = monaco.languages.registerCompletionItemProvider('latex', {
          triggerCharacters: ['\\', '{', ',', '$'],
          provideCompletionItems: (model, position) => {
            const lineContent = model.getLineContent(position.lineNumber);
            const beforeCursor = lineContent.slice(0, position.column - 1);
            const afterCursor = lineContent.slice(position.column - 1);

            if (beforeCursor.endsWith('$') && afterCursor.startsWith('$') && !beforeCursor.endsWith('$$')) {
              return {
                suggestions: LATEX_MATH_MODE_SUGGESTIONS.map((suggestion, index) => ({
                  label: suggestion.label,
                  kind: monaco.languages.CompletionItemKind.Snippet,
                  insertText: suggestion.insertText,
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: suggestion.detail,
                  sortText: suggestion.sortText,
                  preselect: index === 0,
                  range: suggestion.replaceAutoClosedPair
                    ? new monaco.Range(position.lineNumber, position.column - 1, position.lineNumber, position.column + 1)
                    : new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
                })),
              };
            }

            const referenceMatch = beforeCursor.match(/\\(?:auto|eq|pag|v|c|C)?ref\{([^}]*)$/);
            if (referenceMatch) {
              const current = referenceMatch[1].split(',').pop() || '';
              const startColumn = position.column - current.length;
              return {
                suggestions: labelSuggestionsRef.current.map((label) => ({
                  label,
                  kind: monaco.languages.CompletionItemKind.Reference,
                  insertText: label,
                  detail: 'LaTeX label',
                  range: new monaco.Range(position.lineNumber, startColumn, position.lineNumber, position.column),
                })),
              };
            }

            const citationMatch = beforeCursor.match(/\\cite[a-zA-Z*]*\{([^}]*)$/);
            if (citationMatch) {
              const current = citationMatch[1].split(',').pop() || '';
              const startColumn = position.column - current.length;
              return {
                suggestions: citationSuggestionsRef.current.map((key) => ({
                  label: key,
                  kind: monaco.languages.CompletionItemKind.Reference,
                  insertText: key,
                  detail: 'BibTeX key',
                  range: new monaco.Range(position.lineNumber, startColumn, position.lineNumber, position.column),
                })),
              };
            }

            const commandMatch = beforeCursor.match(/\\([A-Za-z]*)$/);
            if (commandMatch) {
              const current = commandMatch[1];
              const startColumn = position.column - current.length - 1;
              return {
                suggestions: LATEX_COMMAND_SUGGESTIONS.map((command) => ({
                  label: command.label,
                  kind: monaco.languages.CompletionItemKind.Snippet,
                  insertText: command.insertText,
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: command.detail,
                  range: new monaco.Range(position.lineNumber, startColumn, position.lineNumber, position.column),
                })),
              };
            }

            return { suggestions: [] };
          },
        });
      }

    },
    [navigateDiagnostic, onCompile, onCursorChange, onSave, onSelectionChange]
  );

  const handleChange = useCallback(
    (val: string | undefined) => {
      // In OT mode, local edits are handled by OT engine, not onChange
      if (isRemoteEditRef.current) return;
      if (isFileSwitchingRef.current) return; // Skip file-switch executeEdits
      onChange(val || '');
    },
    [onChange]
  );

  useEffect(() => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    if (!editorInstance || !monaco) return;

    if (!clipboardActionsAddedRef.current) {
      editorInstance.addAction({
        id: 'clipboard-copy',
        label: 'Copy',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 0,
        run: async (ed) => {
          const selection = ed.getSelection();
          const model = ed.getModel();
          if (!selection || selection.isEmpty() || !model) return;

          const text = model.getValueInRange(selection);
          if (!text) return;

          try {
            await navigator.clipboard.writeText(text);
          } catch {
            // Fallback for older browsers where Clipboard API is unavailable.
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            ed.focus();
          }
        },
      });

      editorInstance.addAction({
        id: 'clipboard-cut',
        label: 'Cut',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 0.1,
        run: async (ed) => {
          if (readOnly) return;

          const selection = ed.getSelection();
          const model = ed.getModel();
          if (!selection || selection.isEmpty() || !model) return;

          const text = model.getValueInRange(selection);
          if (!text) return;

          try {
            await navigator.clipboard.writeText(text);
          } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
          }

          ed.executeEdits('clipboard-cut', [{
            range: selection,
            text: '',
            forceMoveMarkers: true,
          }]);
          ed.setPosition(selection.getStartPosition());
          ed.focus();
        },
      });

      editorInstance.addAction({
        id: 'clipboard-paste',
        label: 'Paste',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 0.2,
        run: async (ed) => {
          if (readOnly) return;

          const selection = ed.getSelection();
          if (!selection) return;

          try {
            const text = await navigator.clipboard.readText();
            if (!text) return;

            ed.executeEdits('clipboard-paste', [{
              range: selection,
              text,
              forceMoveMarkers: true,
            }]);

            const endPosition = ed.getModel()?.getPositionAt(
              ed.getModel()!.getOffsetAt(selection.getStartPosition()) + text.length
            );
            if (endPosition) {
              ed.setPosition(endPosition);
            }
            ed.focus();
          } catch {
            // Ignore clipboard permission failures; keyboard paste still works.
          }
        },
      });

      clipboardActionsAddedRef.current = true;
    }

    if (canComment && onAddComment && !commentActionAddedRef.current) {
      editorInstance.addAction({
        id: 'add-comment',
        label: 'Add comment',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.5,
        run: (ed) => {
          const selection = ed.getSelection();
          if (!selection || selection.isEmpty()) return;
          const model = ed.getModel();
          if (!model) return;
          const text = model.getValueInRange(selection);
          onAddComment({
            startLine: selection.startLineNumber,
            endLine: selection.endLineNumber,
            startColumn: selection.startColumn,
            endColumn: selection.endColumn,
            text,
          });
        },
      });
      commentActionAddedRef.current = true;
    }

    const disposable = editorInstance.onMouseDown((e) => {
      if (!onSelectComment) return;
      if (e.target.type !== monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) return;
      const position = e.target.position;
      if (!position) return;
      const line = position.lineNumber;
      const match = commentRanges.find(
        (range) => line >= range.startLine && line <= range.endLine
      );
      if (match) {
        onSelectComment(match.id);
      }
    });

    const decorations = commentRanges.map((range) => ({
      range: new monaco.Range(
        range.startLine,
        range.startColumn,
        range.endLine,
        range.endColumn
      ),
      options: {
        className: 'comment-highlight',
        isWholeLine: false,
        glyphMarginClassName: 'codicon codicon-comment comment-glyph',
        glyphMarginHoverMessage: { value: 'Comment' },
      },
    }));

    decorationIdsRef.current = editorInstance.deltaDecorations(
      decorationIdsRef.current,
      decorations
    );
    return () => disposable.dispose();
  }, [commentRanges, canComment, onAddComment, onSelectComment, readOnly]);

  useEffect(() => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    if (!editorInstance || !monaco || !selectedCommentId) return;
    const match = commentRanges.find((range) => range.id === selectedCommentId);
    if (!match) return;
    const range = new monaco.Range(
      match.startLine,
      match.startColumn,
      match.endLine,
      match.endColumn
    );
    editorInstance.revealRangeInCenter(range);
    editorInstance.setSelection(range);
  }, [selectedCommentId, commentRanges]);

  // Sync readOnly option when it changes after mount (e.g. async access role resolution)
  useEffect(() => {
    const editorInstance = editorRef.current;
    if (editorInstance?.updateOptions) {
      editorInstance.updateOptions({ readOnly });
    }
  }, [readOnly]);

  // Spellcheck only applies to LaTeX/BibTeX files — code files would produce false positives
  const language = getMonacoLanguage(fileName);
  const isSpellcheckable = language === 'latex' || language === 'bibtex';

  // Spellchecker lifecycle: initialize when editor is ready and enabled, dispose when disabled
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco || !editorReady) return;

    if (!spellcheckEnabled || !isSpellcheckable) {
      if (spellcheckerRef.current) {
        spellcheckerRef.current.dispose();
        spellcheckerRef.current = null;
      }
      const model = editor.getModel();
      if (model) {
        monaco.editor.setModelMarkers(model, 'spellchecker', []);
      }
      return;
    }

    let disposed = false;

    async function initSpellchecker() {
      // Lazily initialize Typo dictionary by fetching from public/
      if (!typoRef.current) {
        const [affData, dicData] = await Promise.all([
          fetch('/dictionaries/en_US/en_US.aff').then(r => r.text()),
          fetch('/dictionaries/en_US/en_US.dic').then(r => r.text()),
        ]);
        if (disposed) return;
        typoRef.current = new Typo('en_US', affData, dicData);
      }
      const dictionary = typoRef.current;

      if (disposed) return;

      // Load custom dictionary from localStorage
      try {
        const stored = localStorage.getItem('spellcheck-custom-dictionary');
        if (stored) {
          const words: string[] = JSON.parse(stored);
          words.forEach(w => customDictionaryRef.current.add(w));
        }
      } catch { /* ignore malformed data */ }

      const spellchecker = getSpellchecker(monaco!, editor!, {
        check: (word: string) =>
          ignoredWordsRef.current.has(word)
          || customDictionaryRef.current.has(word)
          || dictionary.check(word),
        suggest: (word: string) => dictionary.suggest(word),
        tokenize: latexTokenize,
        ignore: (word: string) => {
          ignoredWordsRef.current.add(word);
        },
        addWord: (word: string) => {
          customDictionaryRef.current.add(word);
          try {
            const stored = JSON.parse(localStorage.getItem('spellcheck-custom-dictionary') || '[]');
            stored.push(word);
            localStorage.setItem('spellcheck-custom-dictionary', JSON.stringify(stored));
          } catch { /* ignore storage errors */ }
        },
      });

      if (disposed) {
        spellchecker.dispose();
        return;
      }

      spellcheckerRef.current = spellchecker;

      // Add "Spelling Suggestions..." to right-click context menu
      const contextMenuAction = editor!.addAction({
        id: 'spellchecker.contextMenu',
        label: 'Spelling Suggestions...',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 0,
        precondition: undefined,
        run: (ed) => {
          ed.trigger('spellcheck', 'editor.action.quickFix', {});
        },
      });

      // Chain context menu cleanup into spellchecker dispose
      const originalDispose = spellchecker.dispose;
      spellchecker.dispose = () => {
        contextMenuAction.dispose();
        originalDispose();
      };

      spellchecker.process();
    }

    initSpellchecker();

    return () => {
      disposed = true;
      if (spellcheckerRef.current) {
        spellcheckerRef.current.dispose();
        spellcheckerRef.current = null;
      }
    };
  }, [spellcheckEnabled, isSpellcheckable, editorReady]);

  // Trigger reprocess on content change (debounced)
  useEffect(() => {
    if (!spellcheckEnabled || !spellcheckerRef.current) return;
    const timer = setTimeout(() => {
      spellcheckerRef.current?.process();
    }, 500);
    return () => clearTimeout(timer);
  }, [value, spellcheckEnabled]);

  // OT: Apply remote operations to editor, then sync state for compilation
  useEffect(() => {
    if (!otEnabled || !remoteOps || remoteOps.length === 0) return;
    const editorInstance = editorRef.current;
    if (!editorInstance) return;
    const model = editorInstance.getModel();
    if (!model) return;

    for (const { op } of remoteOps) {
      const edits = otOpToMonacoEdits(op, model);
      if (edits.length === 0) continue;

      isRemoteEditRef.current = true;
      model.pushEditOperations([], edits, () => null);
      isRemoteEditRef.current = false;
    }

    // After applying, sync state so compilation uses current content.
    // At this point the model is already updated, so when React re-renders
    // with the new value prop, Monaco will see model.getValue() === value
    // and will NOT reset (no cursor jump).
    onRemoteContentSync?.(model.getValue());
  }, [otEnabled, remoteOps, onRemoteContentSync]);

  // Render remote cursor and selection decorations for all collaborators
  useEffect(() => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    if (!editorInstance || !monaco) return;

    if (remoteUsers.length === 0) {
      // Clear decorations when no remote users
      if (remoteCursorDecorationIdsRef.current.length > 0) {
        remoteCursorDecorationIdsRef.current = editorInstance.deltaDecorations(
          remoteCursorDecorationIdsRef.current,
          []
        );
      }
      return;
    }

    const decorations: editor.IModelDeltaDecoration[] = [];

    for (const user of remoteUsers) {
      if (!user.cursor) continue;

      // Inject dynamic CSS for this user's color (once per user)
      const styleId = `remote-cursor-${user.userId}`;
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .remote-cursor-${CSS.escape(user.userId)} {
            border-left: 2px solid ${user.color};
            margin-left: -1px;
          }
          .remote-cursor-label-${CSS.escape(user.userId)} {
            background-color: ${user.color};
            color: white;
            padding: 1px 4px;
            font-size: 10px;
            border-radius: 2px;
            white-space: nowrap;
          }
          .remote-selection-${CSS.escape(user.userId)} {
            background-color: ${user.color}22;
          }
        `;
        document.head.appendChild(style);
      }

      // Cursor decoration — colored line with user name label
      decorations.push({
        range: new monaco.Range(
          user.cursor.lineNumber,
          user.cursor.column,
          user.cursor.lineNumber,
          user.cursor.column
        ),
        options: {
          className: `remote-cursor-${user.userId}`,
          beforeContentClassName: `remote-cursor-label-${user.userId}`,
          before: {
            content: user.displayName,
            inlineClassName: `remote-cursor-label-${user.userId}`,
          },
          stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        },
      });

      // Selection decoration — translucent highlight in user's color
      if (user.selection) {
        decorations.push({
          range: new monaco.Range(
            user.selection.startLineNumber,
            user.selection.startColumn,
            user.selection.endLineNumber,
            user.selection.endColumn
          ),
          options: {
            className: `remote-selection-${user.userId}`,
            stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
          },
        });
      }
    }

    remoteCursorDecorationIdsRef.current = editorInstance.deltaDecorations(
      remoteCursorDecorationIdsRef.current,
      decorations
    );
  }, [remoteUsers]);

  useEffect(() => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    const model = editorInstance?.getModel();
    if (!editorInstance || !monaco || !model) return;

    const markers = compileDiagnostics.map((diagnostic) => ({
      startLineNumber: diagnostic.line,
      startColumn: diagnostic.column,
      endLineNumber: diagnostic.endLine,
      endColumn: diagnostic.endColumn,
      message: diagnostic.message,
      severity: diagnostic.severity === 'warning'
        ? monaco.MarkerSeverity.Warning
        : monaco.MarkerSeverity.Error,
    }));

    monaco.editor.setModelMarkers(model, 'compile', markers);

    const firstDiagnostic = compileDiagnostics[0];
    const diagnosticKey = firstDiagnostic
      ? `${firstDiagnostic.file}:${firstDiagnostic.line}:${firstDiagnostic.message}`
      : null;

    if (firstDiagnostic && diagnosticKey !== lastRevealedDiagnosticRef.current) {
      const range = new monaco.Range(
        firstDiagnostic.line,
        firstDiagnostic.column,
        firstDiagnostic.endLine,
        Math.max(firstDiagnostic.column, 2)
      );
      editorInstance.revealRangeInCenter(range);
      lastRevealedDiagnosticRef.current = diagnosticKey;
    }

    if (!firstDiagnostic) {
      lastRevealedDiagnosticRef.current = null;
    }
  }, [compileDiagnostics]);

  useEffect(() => {
    return () => {
      completionProviderDisposableRef.current?.dispose();
      completionProviderDisposableRef.current = null;
    };
  }, []);

  return (
    <div className="h-full w-full">
      <Editor
        height="100%"
        defaultLanguage="latex"
        language={getMonacoLanguage(fileName)}
        value={value}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        theme="vs-light"
        options={{
          readOnly,
          contextmenu: true,
          lightbulb: { enabled: 'on' } as any,
          fontSize: 14,
          fontFamily:
            "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
          lineNumbers: 'on',
          glyphMargin: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: true,
          wordWrap: 'on',
          automaticLayout: true,
          folding: true,
          bracketPairColorization: { enabled: true },
          autoClosingBrackets: 'always',
          autoClosingQuotes: 'always',
          snippetSuggestions: 'bottom',
          renderLineHighlight: 'line',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          padding: {
            bottom: 80,
          },
        }}
      />
    </div>
  );
}
